/**
 * useTeacherActivityCounters — Jun 2026.
 *
 * Teacher twin of `useStudentActivityCounters` (same polling contract,
 * same refresh event). Only fires for role=Teacher; returns null
 * otherwise so DashboardLayout can no-op.
 *
 * Buckets (single lean call → GET /api/teacher/badge-counts):
 *   grading       → submitted assessment attempts awaiting manual marks
 *   excuses       → pending absence excuses on the teacher's classrooms
 *   announcements → unread official admin notifications
 *
 * Refresh:
 *   • Poll every 45s while the tab is visible.
 *   • `mesp:activity-refresh` CustomEvent forces an immediate re-fetch
 *     (shared with the student hook — dispatch after grading/approving).
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { ACTIVITY_REFRESH_EVENT } from './useStudentActivityCounters';

const API = process.env.REACT_APP_BACKEND_URL;
const OPTS = { withCredentials: true };
const POLL_MS = 45000;

export function useTeacherActivityCounters(role) {
  const enabled = role === 'Teacher';
  const [counts, setCounts] = useState({ grading: 0, excuses: 0, announcements: 0, total: 0 });
  const cancelRef = useRef(false);

  const fetchCounts = useCallback(async () => {
    if (!enabled) return;
    try {
      const r = await axios.get(`${API}/api/teacher/badge-counts`, OPTS);
      if (cancelRef.current) return;
      const d = r.data || {};
      setCounts({
        grading: Number(d.grading || 0),
        excuses: Number(d.excuses || 0),
        announcements: Number(d.announcements || 0),
        total: Number(d.total || 0),
      });
    } catch {
      /* fail-quiet — badges simply won't update */
    }
  }, [enabled]);

  useEffect(() => {
    cancelRef.current = false;
    if (!enabled) return () => { cancelRef.current = true; };

    fetchCounts();
    const iv = setInterval(() => {
      if (document.visibilityState === 'visible') fetchCounts();
    }, POLL_MS);
    const onVis = () => { if (document.visibilityState === 'visible') fetchCounts(); };
    const onRefresh = () => fetchCounts();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener(ACTIVITY_REFRESH_EVENT, onRefresh);

    return () => {
      cancelRef.current = true;
      clearInterval(iv);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener(ACTIVITY_REFRESH_EVENT, onRefresh);
    };
  }, [enabled, fetchCounts]);

  return enabled ? counts : null;
}
